/*******************************************************************************
 * @ngdoc    object
 * @module   makeDeposit
 * @name     csm.books.MakeDeposit.Data.type:QuickDepositItem
 * @constructor
 * @param {object=} item  Quick deposit item from database.
 * @description
 * Deposit item added at the time of deposit. `payments` and `saleItemTotals`
 * lists have a single item each and property accessors get and set properties of
 * these items. These properties are for use with the `quickItemDialog` component.
 *******************************************************************************/
/* @ngInject */ export function runQuickDepositItemData(Data, SERVER) {
  Data.QuickDepositItem = function (item) {
    var rv = Object.assign(
      Object.create(Data.QuickDepositItem.prototype),
      new Data.DepositReceiptItem(angular.extend({}, item))
    );
    if (!item) {
      rv.payments.push(new Data.DepositReceiptPaymentItem());
      rv.saleItemTotals.push(new Data.DepositReceiptSalesItem());
      rv.type = rv.TYPE.CASH;
    }
    return rv;
  };

  Object.defineProperties((Data.QuickDepositItem.prototype = {}), {
    /*---------------------------------------------------*/
    /* References to first payment/saleItemTotals items. */
    /*---------------------------------------------------*/
    payment: {
      get: function () {
        return this.payments[0];
      }
    },
    saleItem: {
      get: function () {
        return this.saleItemTotals[0];
      }
    },

    /**
     * @ngdoc       property
     * @name        account
     * @propertyOf  csm.books.MakeDeposit.Data.type:QuickDepositItem
     * @description Sales item total `accountInfo` property for use
     * with `accountSelector` component. Returns/Sets `accountObj`
     * property because returning a new object each time results in
     * errors when this property is bound.
     */
    account: {
      get: function () {
        return this.accountObj;
      },
      set: function (v) {
        this.accountObj = v;
      }
    },

    /**
     * @ngdoc       property
     * @name        amount
     * @propertyOf  csm.books.MakeDeposit.Data.type:QuickDepositItem
     * @description Returns/sets amount of payment and sales items.
     */
    amount: {
      get: function () {
        return this.saleItem.amount;
      },
      set: function (v) {
        this.saleItem.amount = this.payment.amount = v;
      }
    },

    /**
     * @ngdoc       property
     * @name        isQuickItem
     * @propertyOf  csm.books.MakeDeposit.Data.type:QuickDepositItem
     * @description Returns `true` indicating a quick deposit item.
     */
    isQuickItem: {
      get: function () {
        return true;
      },
      set: function (v) {}
    },

    /**
     * @ngdoc       property
     * @name        checkName
     * @propertyOf  csm.books.MakeDeposit.Data.type:QuickDepositItem
     * @description Returns/sets payment item `checkName`.
     */
    checkName: {
      get: function () {
        return this.payment.checkName;
      },
      set: function (v) {
        this.payment.checkName = v;
      }
    },

    /**
     * @ngdoc       property
     * @name        number
     * @propertyOf  csm.books.MakeDeposit.Data.type:QuickDepositItem
     * @description Returns/sets payment item `checkNumber`.
     */
    checkNumber: {
      get: function () {
        return this.payment.checkNumber;
      },
      set: function (v) {
        this.payment.checkNumber = v;
      }
    },

    /**
     * @ngdoc       property
     * @name        memo
     * @propertyOf  csm.books.MakeDeposit.Data.type:QuickDepositItem
     * @description Returns/sets sale item `description`.
     */
    memo: {
      get: function () {
        return this.saleItem.description;
      },
      set: function (v) {
        this.saleItem.description = v;
      }
    },

    /**
     * @ngdoc       property
     * @name        type
     * @propertyOf  csm.books.MakeDeposit.Data.type:QuickDepositItem
     * @description Quick deposit item type.
     * Returns property of `TYPE` based on payment `subType`.
     * Sets payment `type` and `subType`.
     */
    type: {
      get: function () {
        switch (this.payment.subType) {
          case SERVER.BooksDBConstants.LIS_CASH_RECEIVED:
            return this.TYPE.CASH;
          case SERVER.BooksDBConstants.LIS_CHECK_RECEIVED:
            return this.TYPE.CHECK;
        }
      },

      set: function (t) {
        this.payment.subType = t;
        this.payment.type = SERVER.BooksDBConstants.LIT_MONEY_RECEIVED;
      }
    },

    /**************************************************************
  * TYPE
  /**
  * @ngdoc       object
  * @name        TYPE
  * @propertyOf  csm.books.MakeDeposit.Data.type:QuickDepositItem
  * @description 
  * `type` property values for quick deposit items.
  *   * `CASH`   - Cash received.
  *   * `CHECK`  - Check received.
  **************************************************************/
    TYPE: {
      value: {
        CASH: SERVER.BooksDBConstants.LIS_CASH_RECEIVED,
        CHECK: SERVER.BooksDBConstants.LIS_CHECK_RECEIVED
      }
    }
  });

  /*****************************************************************************
   * toJSON */
  /**
   * Create the properties to be used when serializing to JSON. Since all the
   * properties on this object are created from defineProperties, we need to make
   * sure they are enumerable or they will not be serialized.
   *****************************************************************************/
  Data.QuickDepositItem.prototype.toJSON = function () {
    return {
      id: this.id,
      payments: this.payments,
      saleItemTotals: this.saleItemTotals,
      account: this.account,
      amount: this.amount,
      isQuickItem: this.isQuickItem,
      name: this.checkName,
      number: this.checkNumber,
      memo: this.memo,
      type: this.type
    };
  };

  return Data;
};
